// =====================================================
// CHANGE RECORD — the Word writer's own account of the changes it writes in the body.
//
// As the writer builds the body it notes, for each change, which compared paragraph (block) it
// belongs to, its kind and move, where its words sit in the source paragraph, which diff entries
// it covers, and the revision ids it carries. The change list (--json), the summary page and the
// review page read this record instead of working the changes out again from the finished file
// (until stage 4 of the engine cleanup each matched the file's text back to the diff character by
// character).
//
// How the record rides through the writer: every body block is written with an attribute naming
// its paragraph (fl-b), and every revision the writer mints gets one naming its change (fl-c),
// added when the body's revision ids are numbered (numberBodyRevisions). The package steps
// after that leave unknown attributes alone, so the labels end up on the final elements. The last
// step takes them off, so the saved file is exactly what it was, and keeps the labelled body for
// the change list (`labeledBody`).
//
// The record also holds a change the writer leaves to the revised draft's own tracked changes
// (Combine) or does not write in this mode (markup): `written: false`. The summary counts those
// the same as the rest, so every output counts the same changes.
// =====================================================

export type RecordedType = 'insert' | 'delete' | 'move-from' | 'move-to' | 'format';

export interface RecordedChange {
  /** Its index in `changes` (the fl-c label). */
  n: number;
  /** The diff paragraph it belongs to. */
  block: number;
  /** Unique within the block: 'w' the whole block, 'd' / 'i' the old and new block of a block
   *  replaced whole, 'm' a paragraph mark (split or merge), 'f' formatting, 'r<k>' a run of
   *  changed words, 'mt' a moved paragraph's moved words, 'e<k>' a mark or object inserted or
   *  deleted on its own; inside a table row the same, after 'c<cell>.' (and 'p<k>.', a paragraph
   *  of the cell). */
  key: string;
  type: RecordedType;
  /** 'words': changed words (or a cell's, or a mark or object between words); 'whole': a whole
   *  block; 'mark': a paragraph mark; 'format': a formatting change; 'spacing': whitespace alone
   *  (a tab or line break that differs, tracked so Reject All gives it back; not counted as a
   *  change, and not drawn on the page). */
  scope: 'words' | 'whole' | 'mark' | 'format' | 'spacing';
  cell?: number;
  moveId?: number;
  /** Characters [start, end) of the change in its source paragraph's text, on its side (the
   *  original's for deletions and moved-from words, else the revised draft's); words only. */
  span?: [number, number];
  /** The body paragraph's diff entries it covers (an edited paragraph's words). */
  entries?: number[];
  /** The changed text in the diff, its non-space characters (a check on what was written). */
  chars?: string;
  /** False when the writer did not write it (see the header). */
  written: boolean;
  /** How many revision ids it was given (none in markup). */
  minted: number;
}

export interface ChangeRecord {
  changes: RecordedChange[];
  /** The final word/document.xml with its labels. */
  labeledBody: string;
}

// ── recording (the writer) ──────────────────────────────────────────────────

interface Recorder {
  changes: RecordedChange[];
  byKey: Map<string, RecordedChange>;
  byId: Map<number, RecordedChange>;
  block: number;
  current: RecordedChange | null;
}
let rec: Recorder | null = null;

/** Start a record (generateRedlineDocx, before the body). */
export function startRecord(): void {
  rec = { changes: [], byKey: new Map(), byId: new Map(), block: -1, current: null };
}
/** Stop recording; the changes recorded. */
export function endRecord(): { changes: RecordedChange[]; byId: Map<number, RecordedChange> } {
  const r = rec || { changes: [], byId: new Map() };
  rec = null;
  return { changes: r.changes, byId: r.byId };
}
/** The changes recorded so far (the summary page is built before the file is finished). */
export function recSnapshot(): RecordedChange[] { return rec ? rec.changes : []; }
/** The block being written (a diff paragraph index; -1 outside the compared body). */
export function recBlock(block: number): void {
  if (!rec) return;
  rec.block = block;
  rec.current = null;
}
export function recCurrentBlock(): number { return rec ? rec.block : -1; }

/** The change `key` of the current block, declared now if new; revision ids minted from now on
 *  belong to it (until the next call). Returns it, or null when not recording. */
export function recChange(key: string, c: { type: RecordedType; scope: RecordedChange['scope']; cell?: number; moveId?: number; span?: [number, number]; entries?: number[]; chars?: string; written?: boolean }): RecordedChange | null {
  if (!rec || rec.block < 0) return null;
  const k = `${rec.block}:${key}`;
  let ch = rec.byKey.get(k);
  if (!ch) {
    ch = { n: rec.changes.length, block: rec.block, key, type: c.type, scope: c.scope, written: c.written !== false, minted: 0 };
    if (c.cell != null) ch.cell = c.cell;
    if (c.moveId != null) ch.moveId = c.moveId;
    if (c.span) ch.span = c.span;
    if (c.entries) ch.entries = [...c.entries];
    if (c.chars != null) ch.chars = c.chars;
    rec.changes.push(ch);
    rec.byKey.set(k, ch);
  } else {
    // a change written in several pieces (one run of words split by a bookmark): one change
    if (c.written !== false) ch.written = true;
    if (c.chars != null && ch.chars == null) ch.chars = c.chars;
    if (c.moveId != null && ch.moveId == null) ch.moveId = c.moveId;
    if (c.span) ch.span = ch.span ? [Math.min(ch.span[0], c.span[0]), Math.max(ch.span[1], c.span[1])] : c.span;
    if (c.entries) ch.entries = [...new Set([...(ch.entries || []), ...c.entries])].sort((a, b) => a - b);
  }
  rec.current = ch;
  return ch;
}
/** The change ids are minted for (null: none). */
export function recSetCurrent(ch: RecordedChange | null): void { if (rec) rec.current = ch; }
export function recGetCurrent(): RecordedChange | null { return rec ? rec.current : null; }
/** The change a minted revision id belongs to (its fl-c label), while recording. */
export function recLabelOf(id: number): number | undefined { return rec?.byId.get(id)?.n; }
/** Called for every revision id the writer mints. */
export function recMinted(id: number): void {
  if (rec && rec.current) { rec.byId.set(id, rec.current); rec.current.minted++; }
}

/** The changes a summary counts: in a tracked file the ones written with revisions (a change left
 *  to the revised draft's own tracked changes too), in markup every one recorded. */
export function countedChanges(changes: RecordedChange[], tracked: boolean): RecordedChange[] {
  return changes.filter(c => c.scope !== 'format' && c.scope !== 'spacing' && (!tracked || c.minted > 0 || !c.written));
}
/** The tracked spacing changes (tabs and line breaks), which the summary names apart. */
export function spacingChanges(changes: RecordedChange[]): number {
  return changes.filter(c => c.scope === 'spacing' && c.minted > 0).length;
}

// ── labels ──────────────────────────────────────────────────────────────────

/** Label every top-level element of a body fragment as block `label` (fl-b). */
export function labelBlocks(xml: string, label: string): string {
  let depth = 0, out = '', last = 0;
  const re = /<(\/?)([A-Za-z_][\w.-]*(?::[\w.-]+)?)\b([^>]*?)(\/?)>|<!--[\s\S]*?-->|<\?[\s\S]*?\?>/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(xml))) {
    if (m[2] === undefined) continue;   // a comment or processing instruction
    const [, close, name, , self] = m;
    if (close) { depth = Math.max(0, depth - 1); continue; }
    if (depth === 0 && !m[0].includes(' fl-b="')) {
      const at = m.index + 1 + name.length;
      out += xml.slice(last, at) + ` fl-b="${label}"`;
      last = at;
    }
    if (!self) depth++;
  }
  return last ? out + xml.slice(last) : xml;
}

const LABEL_RE = / fl-[bc]="[^"]*"/g;
/** The body without its labels: the file as it is saved. */
export function stripLabels(xml: string): string {
  return xml.indexOf(' fl-') < 0 ? xml : xml.replace(LABEL_RE, '');
}

// ── reading (the change list, the summary) ─────────────────────────────────

const RECORDS = new WeakMap<object, ChangeRecord>();
/** Attach the record to the file the writer returned. */
export function attachRecord(blob: object, record: ChangeRecord): void { RECORDS.set(blob, record); }
/** The record of a file generateRedlineDocx returned (the Blob itself, not its bytes). */
export function changeRecordOf(blob: object): ChangeRecord | undefined { return RECORDS.get(blob); }
